import type {CreditsPillContent} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPillTheme} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import React from 'react';
import Tooltip from 'react-bootstrap/Tooltip';
import styles from './credits-pill.module.scss';
import {Pill, PILL_BACKGROUND_COLOR, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {Text, TextSize} from '@Components/text';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {abbreviateNumber, formatNumber} from '@Utils/number.util';
import type {OverlayInjectedProps} from 'react-bootstrap/Overlay';

interface CreditsPillProps {
  showPill: boolean;
  theme: CreditsPillTheme;
  content: CreditsPillContent;
}

export function CreditsPill({showPill, theme, content}: CreditsPillProps): ReactElement {
  const actionCost = content.actionCost;
  const remainingCredits = content.remainingCredits;

  const getTooltipContent = (): ReactElement => {
    return (
      <div className="flex-center flex-v-row">
        <Text
          dangerouslySetInnerHTML
          size={TextSize.XXSMALL}
          val={actionCost <= 1 ? window.i18next.t('pmwjs_will_cost_1_credit') : window.i18next.t('pmwjs_will_cost_x_credit', {creditsCost: actionCost, pluralSuffix: 's'})}
        />
        <Text size={TextSize.XXSMALL} val={window.i18next.t('pmwjs_curently_credit_credits', {credit: formatNumber(remainingCredits)})} />
      </div>
    );
  };

  const getCreditsPillTooltip = (): ReactElement<OverlayInjectedProps, string> => {
    return (
      <Tooltip className={`spacing-m-b-2`}>
        {getTooltipContent()}
      </Tooltip>
    );
  };

  const getPill = (): ReactElement => {
    return (
      <div>
        <Pill
          content={{
            type: PILL_CONTENT_TYPE.TEXT,
            text: abbreviateNumber(remainingCredits),
            textColor: theme === CreditsPillTheme.WARNING ? 'content-premium' : 'content-secondary',
            textSize: TextSize.XXXSMALL,
          }}
          background={theme === CreditsPillTheme.WARNING ? PILL_BACKGROUND_COLOR.PREMIUM_20 : PILL_BACKGROUND_COLOR.SECONDARY_20}
          size={PILL_SIZE.XSMALL}
          className={`spacing-p-l-2 spacing-p-r-2 ${styles.pill} ${showPill ? '' : styles.hidden} ${theme === CreditsPillTheme.WARNING ? styles.pillHoverPremium : styles.pillHoverSecondary}  `}
        />
      </div>
    );
  };

  const getCreditsPill = (): ReactElement => {
    return (
      <OverlayTrigger trigger={['hover', 'focus', 'click']} overlay={getCreditsPillTooltip()} delay={{show: 200, hide: 200}} placement="top" show={showPill ? undefined : false}>
        {getPill()}
      </OverlayTrigger>
    );
  };

  return getCreditsPill();
}
